/**
 * AudioManager - Manages audio playback for GLB scenes.
 */

import * as THREE from 'three'
import { debugError, debugWarn } from '../utils/debug'

const DEFAULT_VOLUME = 1

export class AudioManager
{
  loopingSounds = []
  listener = null
  defaultSound = null
  audioLoader = null
  loadedSounds = {}

  constructor(camera)
  {
    this.loopingSounds = []
    this.loadedSounds = {}
    this.listener = new THREE.AudioListener()
    camera.add(this.listener)
    this.defaultSound = new THREE.Audio(this.listener)
    this.audioLoader = new THREE.AudioLoader()
  }

  playSound = (parentObject, loop = false, audioObj = null) =>
  {
    const userData = parentObject.userData
    if (!userData?.mediaSrc) return

    // Ensure AudioContext is running
    const context = this.listener.context
    if (context.state === 'suspended') context.resume()

    const audio = audioObj ?? this.defaultSound
    const source = userData.mediaSrc
    const volume = userData.mediaVolume ?? DEFAULT_VOLUME

    audio.setLoop(loop)
    audio.setVolume(volume)

    if (audio.isPlaying) audio.stop()

    // Use cached buffer or load new one
    if (source in this.loadedSounds)
    {
      audio.setBuffer(this.loadedSounds[ source ])
      audio.play()
      return
    }

    this.audioLoader.load(
      source,
      (buffer) =>
      {
        this.loadedSounds[ source ] = buffer
        audio.setBuffer(buffer)
        audio.play()
      },
      undefined,
      (err) => debugError('[AudioManager] Error loading sound:', source, err)
    )
  }

  playLoopingSounds()
  {
    this.loopingSounds.forEach(soundObject =>
    {
      const audio = new THREE.Audio(this.listener)
      this.playSound(soundObject, true, audio)
    })
  }

  getLoopingSounds()
  {
    return this.loopingSounds
  }

  parseSounds(object)
  {
    const userData = object.userData
    if (!userData) return
    if (!('Media' in userData)) return
    if (userData.mediaType !== 'Audio') return

    if (!('mediaTrigger' in userData))
    {
      debugWarn('[AudioManager] No media trigger found on Audio object')
      return
    }

    if (userData.mediaTrigger === 'Looping')
    {
      this.loopingSounds.push(object)
    }
  }
}
